import {Text} from '@dropbox/dig-components/typography';
import {atoms, Stack} from '@dropbox/dig-foundations';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {type ProductRoadmapCreate, ProductRoadmapService, type Workstream} from 'client';
import {t} from 'i18next';
import {useId, useState} from 'react';
import {getService} from 'utilities';
import {productRoadmapQueryKey} from 'views/productRoadmap/hooks';
import {invalidateNavTrees} from 'views/workstreams/page/useNavTree';
import {logWorkstreamsInteraction} from 'views/workstreams/page/workstreamsAnalytics';

import {WorkstreamModal} from './WorkstreamModal';
import {WorkstreamModalField} from './WorkstreamModalField';
import {
  isWorkstreamProductRoadmapUrl,
  roadmapSaveErrorMessage,
  supportsEmbeddedPreview,
} from './WorkstreamRoadmapLinkForm';

export const WorkstreamProductRoadmapModal = ({
  workstream,
  onClose,
}: {
  workstream: Pick<Workstream, 'id'>;
  onClose: () => void;
}) => {
  const formId = useId();
  const messageId = `${formId}-url-message`;
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const {mutate, isPending, error, reset} = useMutation({
    mutationFn: (document: Pick<ProductRoadmapCreate, 'title' | 'url'>) =>
      getService(ProductRoadmapService).createProductRoadmapApiV1ProductRoadmapPost({
        workstream_id: workstream.id,
        ...document,
      }),
    retry: false,
    onSuccess: () => {
      logWorkstreamsInteraction('roadmap_added', {workstream_id: workstream.id});
      onClose();
      invalidateNavTrees();
      return queryClient.invalidateQueries({
        queryKey: productRoadmapQueryKey({workstreamId: workstream.id}),
        exact: true,
      });
    },
  });
  const isValid = isWorkstreamProductRoadmapUrl(url);
  const isInvalid = url.trim().length > 0 && !isValid;
  const canSubmit = isValid && name.trim().length > 0;
  const showPreviewWarning = isValid && !supportsEmbeddedPreview(url);
  const errorMessage = isInvalid
    ? t('workstream_product_roadmap_modal_invalid_link')
    : error
      ? roadmapSaveErrorMessage(error)
      : undefined;
  const inputMessage =
    errorMessage ??
    (showPreviewWarning ? t('workstream_product_roadmap_preview_unsupported') : undefined);

  return (
    <WorkstreamModal
      open
      width="standard"
      title={t('workstream_product_roadmap_add')}
      description={t('workstream_product_roadmap_description')}
      onClose={() => {
        if (!isPending) onClose();
      }}
      actionLabel={t('add')}
      actionProps={{
        type: 'submit',
        form: formId,
        disabled: !canSubmit || isPending,
        isLoading: isPending,
      }}
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit && !isPending) mutate({title: name.trim(), url: url.trim()});
        }}
      >
        <Stack gap="16">
          <WorkstreamModalField
            label={t('name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              reset();
            }}
            autoFocus
            disabled={isPending}
          />
          <Stack gap="8">
            <WorkstreamModalField
              label={t('url')}
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                reset();
              }}
              placeholder="https://"
              disabled={isPending}
              isInvalid={isInvalid}
              aria-describedby={inputMessage ? messageId : undefined}
            />
            {inputMessage && (
              <Text
                id={messageId}
                size="small"
                className={atoms({
                  color: errorMessage ? 'Alert Base' : 'Warning Base',
                  display: 'block',
                  marginTop: '8',
                })}
                role={errorMessage ? 'alert' : 'status'}
              >
                {inputMessage}
              </Text>
            )}
          </Stack>
        </Stack>
      </form>
    </WorkstreamModal>
  );
};
